import { UserManager, ErrorResponse, ErrorTimeout } from "oidc-client-ts";

// Seconds to wait after the 1st, 2nd, 3rd... failed grant (the last applies to every later one).
const GRANT_BACKOFF_SECONDS = [5, 20, 40, 60];

// One silent renewal at a time per tab. The scheduled renew, the request interceptor and extensions
// all end up in signinSilent; sharing the in-flight call keeps a rotated refresh token from being
// spent twice, and remembering a refused one keeps a dead token from being retried on every request.
export class TabUserManager extends UserManager {
    _renewal = null;
    _refusedRefreshToken = null;
    _renewalDisabled = false;
    _grantFailures = 0;
    _grantRetryAt = 0;
    _grantError = null;

    signinSilent(args) {
        if (this._renewalDisabled) return Promise.reject(new Error("This page doesn't renew"));
        // A caller that joins an in-flight renewal gets the first caller's request; its args are ignored.
        this._renewal ??= super.signinSilent(args).finally(() => { this._renewal = null; });
        return this._renewal;
    }

    isRenewing() {
        return this._renewal !== null;
    }

    // A framed page shares its parent's chain and must never spend its refresh token.
    disableRenewal() {
        this._renewalDisabled = true;
    }

    canRenew(user) {
        if (this._renewalDisabled) return false;
        if (this.isRenewing()) return true;
        const token = user?.refresh_token;
        return !!token && token !== this._refusedRefreshToken;
    }

    // signinSilent's only refresh-token send. Marking here means only a token that was actually sent
    // is remembered, and no extra user read (getUser re-arms the expiring timer) is needed.
    // Reads re-arm the expiring timer, and the scheduled renew skips canRenew, so a refused token is
    // also refused here instead of being sent again.
    async _useRefreshToken(args) {
        if (args.state.refresh_token === this._refusedRefreshToken) {
            throw new ErrorResponse({ error: "invalid_grant", error_description: "Refresh token already refused" });
        }
        // After a grant that failed without a refusal (offline, a 5xx the browser reports as a TypeError, an
        // HTML body) the STS is left alone for a while; callers get that failure back without a request.
        if (this._grantError && Date.now() < this._grantRetryAt) throw this._grantError;
        try {
            // oidc-client-ts passes timeoutInSeconds: undefined here, which switches the token POST's timeout off.
            const response = await super._useRefreshToken({ ...args, timeoutInSeconds: args.timeoutInSeconds ?? this.settings.silentRequestTimeoutInSeconds });
            this._grantFailures = 0;
            this._grantError = null;
            return response;
        } catch (error) {
            // Only the STS saying no burns the token; timeouts and network errors can be retried.
            if (error instanceof ErrorResponse) {
                this._refusedRefreshToken = args.state.refresh_token;
            } else if (!(error instanceof ErrorTimeout)) {
                // Timeouts are already bounded by single flight and the request timeout.
                const wait = GRANT_BACKOFF_SECONDS[Math.min(this._grantFailures, GRANT_BACKOFF_SECONDS.length - 1)];
                this._grantFailures++;
                this._grantRetryAt = Date.now() + wait * 1000;
                this._grantError = error;
            }
            throw error;
        }
    }
}
